import { format, addDays, isBefore, isAfter, setHours, setMinutes, parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { TIMEZONE } from "../../../utils/constants";
import { useRecords } from "../../../api/useRecords";
import { createCalendarFilter } from "../../../components/calendar/calendarUtils";
import { CustomEventType, Schedule, FormattedSchedule, CategoryKey, CategoryWithTotals, CategoryUser, ServiceWithTotals, ServiceDetail } from "./types";
import { weekDays, CATEGORIES_CONFIG, CLINIC_BREAKS } from "../../../utils/constants";
import { getISODay } from "date-fns";
import { mapValues, orderBy, groupBy, sortBy } from "lodash";
import { useStore } from "../../../store/store";
import { getRelateServicesFromConstants } from "../../../utils/getRelatedServicesFromConstants";
import { Holiday } from "../../../api/usePortugalHolidays";

export const workDays = weekDays.slice(0, 6);

export type DayTimeOffInput = { type: string; notes: string; start: Date | null; end: Date | null };

// Selected days must carry real times — the credited vacation/absence hours are exactly what is stored,
// so no default is ever applied here.
export const findTimeOffDaysWithInvalidTimes = (timeOffs: Record<string, DayTimeOffInput>) => {
    const invalidDays = Object.entries(timeOffs)
        .filter(([, dayInput]) => Boolean(dayInput?.type))
        .filter(([, dayInput]) => !dayInput.start || !dayInput.end || dayInput.start >= dayInput.end)
        .map(([day]) => day);
    return invalidDays;
};

export const createFormattedTimeOffs = (
    startDate: Date,
    endDate: Date,
    user: string,
    clinic: string,
    timeOffs: Record<string, DayTimeOffInput>
) => {
    const adjustedEndDate = new Date(endDate);
    adjustedEndDate.setHours(23, 59, 59, 999);

    const formattedTimeOffs: Array<any> = [];

    let currentDate = startDate;

    while (isBefore(currentDate, adjustedEndDate) || currentDate.getTime() === adjustedEndDate.getTime()) {
        const dayOfWeek = format(currentDate, "EEEE");
        const dayTimeOff = timeOffs[dayOfWeek];
        const iterationDate = new Date(currentDate);

        if (dayTimeOff && dayTimeOff.type && dayTimeOff.start && dayTimeOff.end) {
            const pickedStart = new Date(dayTimeOff.start);
            const pickedEnd = new Date(dayTimeOff.end);
            const slotStartDateTime = setMinutes(setHours(iterationDate, pickedStart.getHours()), pickedStart.getMinutes());
            const slotEndDateTime = setMinutes(setHours(iterationDate, pickedEnd.getHours()), pickedEnd.getMinutes());

            formattedTimeOffs.push({
                start_date: slotStartDateTime.toISOString(),
                end_date: slotEndDateTime.toISOString(),
                user: user,
                clinic_id: clinic,
                type: dayTimeOff.type,
                notes: dayTimeOff.notes || "",
            });
        }

        currentDate = addDays(currentDate, 1);
    }

    return formattedTimeOffs;
};

export const createFormattedSchedules = (
    startDate: Date,
    endDate: Date,
    user: string,
    clinic: string,
    schedules: Schedule,
    room: number
) => {
    const adjustedEndDate = new Date(endDate);
    adjustedEndDate.setHours(23, 59, 59, 999);

    const formattedSchedules: Array<FormattedSchedule> = [];

    let currentDate = startDate;

    while (isBefore(currentDate, adjustedEndDate) || currentDate.getTime() === adjustedEndDate.getTime()) {
        const dayOfWeek = format(currentDate, "EEEE");
        const daySchedules = schedules[dayOfWeek];
        const iterationDate = new Date(currentDate);

        daySchedules.forEach((slot) => {
            if (slot.start && slot.end && slot.services) {
                const startDateTime = new Date(slot.start);
                const endDateTime = new Date(slot.end);

                const startHour = startDateTime.getHours();
                const startMinute = startDateTime.getMinutes();
                const endHour = endDateTime.getHours();
                const endMinute = endDateTime.getMinutes();

                const slotStartDateTime = setMinutes(setHours(iterationDate, startHour), startMinute);
                const slotEndDateTime = setMinutes(setHours(iterationDate, endHour), endMinute);

                const isWithinDateRange = !isBefore(slotStartDateTime, startDate) && !isAfter(slotEndDateTime, adjustedEndDate);

                if (isWithinDateRange) {
                    formattedSchedules.push({
                        start_date: slotStartDateTime.toISOString(),
                        end_date: slotEndDateTime.toISOString(),
                        user: user,
                        clinic_id: clinic,
                        room: room,
                        services: slot.services.map((service) => ({
                            schedules_id: "+",
                            services_id: { id: service.id },
                        })),
                        notes: slot.notes || "",
                    });
                }
            }
        });

        currentDate = addDays(currentDate, 1);
    }

    return formattedSchedules;
};

export const mapScheduleRecordToEvent = (schedule: any, clinicColorMap: Record<number, string> = {}): CustomEventType => {
    const serviceDetails = (Array.isArray(schedule.services) ? schedule.services : []).map((service: any) => ({
        id: service.services_id?.id,
        name: service.services_id?.name,
        maxDuration: service.services_id?.max_duration,
        color: service.services_id?.color || "#44749a",
    }));

    const event = {
        id: schedule.id,
        clinicId: schedule.clinic_id,
        clinicColor: clinicColorMap[schedule.clinic_id],
        title: serviceDetails.map((s: any) => s.name).join(", ") || "Unspecified",
        services: serviceDetails.map((s: any) => s.id),
        serviceDetails,
        start: new Date(schedule.start_date),
        end: new Date(schedule.end_date),
        notes: schedule.notes,
        userId: schedule.user?.id,
        userColor: schedule.user?.technician_id?.[0]?.color,
        userName: `${schedule.user?.first_name} ${schedule.user?.last_name}`,
        userAvatar: schedule?.user?.avatar?.id,
        type: "schedule",
        allDay: schedule.start_date === schedule.end_date,
        room: schedule.room || 9,
    } as CustomEventType;

    return event;
};

export const mapTimeOffRecordToEvent = (timeOff: any, clinicColorMap: Record<number, string> = {}): CustomEventType => {
    const event = {
        id: timeOff.id,
        clinicId: timeOff.clinic_id,
        clinicColor: clinicColorMap[timeOff.clinic_id],
        title: "",
        start: new Date(timeOff.start_date),
        end: new Date(timeOff.end_date),
        notes: timeOff.notes,
        userId: timeOff.user?.id,
        userColor: "#e63131",
        userName: `${timeOff.user?.first_name} ${timeOff.user?.last_name}`,
        userAvatar: timeOff?.user?.avatar?.id,
        type: "time_off",
        time_off_type: timeOff.type,
        allDay: true,
        room: 10,
    } as unknown as CustomEventType;

    return event;
};

export const buildClinicColorMap = (clinics: Array<any> | undefined) => {
    const map = (clinics || []).reduce((acc: Record<number, string>, clinic: any) => {
        acc[clinic.id] = clinic.color;
        return acc;
    }, {});
    return map;
};

export const useStaffAvailability = (filter: any, clinics?: Array<any>) => {
    const filters = createCalendarFilter(filter);

    const { data, status, refetch } = useRecords("schedules", "*,services.*.*,user.*.*", {
        extraOptions: { filter: filters, pageSize: -1 },
    });

    const { data: timeOff, status: timeOffStatus } = useRecords("time_off", "*,user.*.*", {
        extraOptions: { filter: filters, pageSize: -1 },
    });

    const clinicColorMap = buildClinicColorMap(clinics);

    const mappedEvents = data?.items.map((schedule: any) => mapScheduleRecordToEvent(schedule, clinicColorMap)) || [];

    const scheduleEvents = orderBy(mappedEvents, ["userName"], ["asc"]) as CustomEventType[];

    const timeOffEvents =
        timeOff?.items.map((timeOffItem: any) => mapTimeOffRecordToEvent(timeOffItem, clinicColorMap)) ||
        ([] as CustomEventType[]);

    const isLoading = status === "loading" || timeOffStatus === "loading";
    const isError = status === "error" || timeOffStatus === "error";

    const events = [...scheduleEvents, ...timeOffEvents] as CustomEventType[];

    return { events, isLoading, isError, refetch };
};

export const calculateHours = (startDate: Date, endDate: Date): number => {
    const diffInHours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
    return Number(diffInHours.toFixed(1));
};

export const calculateTotalHours = (events: Array<CustomEventType>): number => {
    const totalHours = events.reduce((total, event) => {
        return total + calculateHours(new Date(event.start), new Date(event.end));
    }, 0);

    return Number(totalHours.toFixed(1));
};



export const getScheduleTotals = (userEvents: Array<CustomEventType>) => {
    const dailyTotals = workDays.reduce((acc, day) => {
        acc[day] = 0;
        return acc;
    }, {} as Record<string, number>);

    let weeklyTotal = 0;

    userEvents.forEach((event) => {
        const dayIndex = getISODay(event.start) - 1;
        const day = weekDays[dayIndex];
        const hours = event.type === "time_off" ? 0 : calculateHours(event.start, event.end);

        dailyTotals[day] += hours;
        weeklyTotal += hours;
    });

    return { dailyTotals, weeklyTotal };
};

type WithUserName = { userName: string };

export const groupEventsByUser = <T extends CustomEventType>(events: Array<T>): Record<string, CategoryUser> => {
    const result = events.reduce((acc, event) => {
        if (!acc[event.userId]) {
            acc[event.userId] = {
                userName: event.userName,
                userColor: event.userColor,
                userId: event.userId,
                userAvatar: event.userAvatar,
                events: [],
            };
        }
        acc[event.userId].events.push(event);
        return acc;
    }, {} as Record<string, CategoryUser>);

    return result;
};

export const sortByUserName = <T extends WithUserName>(items: T[] | Record<string, T>): Array<T> => {
    const itemsArray = Array.isArray(items) ? items : Object.values(items);

    const result = itemsArray.sort((a, b) => a.userName.localeCompare(b.userName, "pt-PT"));

    return result;
};

export const sortByTotalHoursDesc = <T extends { totalHours: number }>(items: Array<T>) => {
    const sorted = [...items].sort((a, b) => b.totalHours - a.totalHours);
    return sorted;
}

export const categorizeEvents = (events: Array<CustomEventType>): CategoryWithTotals => {
    const categories = mapValues(CATEGORIES_CONFIG, (category) => ({
        ...category,
        users: {},
        dailyTotals: {},
        possibleSessions: {},
    })) as CategoryWithTotals;

    // Initialize dailyTotals for each category
    Object.keys(categories).forEach((categoryKey) => {
        workDays.forEach((day) => {
            categories[categoryKey as CategoryKey].dailyTotals[day] = 0;
        });
    });

    // Process events
    events.forEach((event) => {
        const categoryKey = determineEventCategoryKey(event.services);
        const category = categories[categoryKey];

        // Add user if not exists
        if (!category.users[event.userId]) {
            category.users[event.userId] = {
                userName: event.userName,
                userAvatar: event.userAvatar,
                userColor: event.userColor,
                userId: event.userId,
                events: [],
            };
        }

        // Add event to user
        category.users[event.userId].events.push(event);

        // Update daily totals
        const dayOfWeek = format(event.start, "EEEE");
        const hours = calculateHours(event.start, event.end);
        category.dailyTotals[dayOfWeek] = (category.dailyTotals[dayOfWeek] ?? 0) + hours;
    });

    // Calculate possible sessions and sort users
    Object.entries(categories).forEach(([categoryKey, category]) => {
        // Calculate possible sessions
        workDays.forEach((day) => {
            category.possibleSessions[day] = calculatePossibleSessions(
                category.dailyTotals[day],
                category.duration,
                category.timeForSessions
            );
        });

        category.possibleSessions.weekly = calculatePossibleSessions(
            Object.values(category.dailyTotals).reduce((acc, curr) => acc + curr, 0),
            category.duration,
            category.timeForSessions
        );

        // Sort users within each category
        const sortedUsers = sortByUserName(category.users);
        category.users = Object.fromEntries(sortedUsers.map((user) => [user.userId, user]));
    });

    return categories;
};

const determineEventCategoryKey = (eventServices: Array<number> = []): CategoryKey => {
    const foundCategory = Object.entries(CATEGORIES_CONFIG).find(([_, config]) =>
        eventServices.some((service: number) => {
            const relatedServicesIds = getRelateServicesFromConstants(config.key as "nfb" | "psy" | "reports");
            return relatedServicesIds.includes(service);
        })
    );

    return (foundCategory?.[0] as CategoryKey) || "others";
};

export const groupEventsByService = (events: Array<CustomEventType>): ServiceWithTotals => {
    const result: ServiceWithTotals = {};

    events.forEach((event) => {
        const eventServiceDetails = event.serviceDetails || [];

        eventServiceDetails.forEach((service: ServiceDetail) => {
            if (!result[service.id]) {
                result[service.id] = {
                    id: service.id,
                    name: service.name,
                    maxDuration: service.maxDuration,
                    color: service.color,
                    users: {},
                    dailyTotals: workDays.reduce((acc, day) => ({ ...acc, [day]: 0 }), {} as Record<string, number>),
                    possibleSessions: {},
                };
            }

            const serviceData = result[service.id];

            if (!serviceData.users[event.userId]) {
                serviceData.users[event.userId] = {
                    userName: event.userName,
                    userAvatar: event.userAvatar,
                    userColor: event.userColor,
                    userId: event.userId,
                    events: [],
                };
            }

            serviceData.users[event.userId].events.push(event);

            const dayOfWeek = format(event.start, "EEEE");
            const hours = calculateHours(event.start, event.end);
            serviceData.dailyTotals[dayOfWeek] = (serviceData.dailyTotals[dayOfWeek] ?? 0) + hours;
        });
    });

    Object.keys(result).forEach((serviceIdKey) => {
        const serviceId = Number(serviceIdKey);
        const serviceData = result[serviceId];

        workDays.forEach((day) => {
            serviceData.possibleSessions[day] = calculatePossibleSessions(
                serviceData.dailyTotals[day],
                serviceData.maxDuration,
                1
            );
        });

        const dailyTotalsValues = Object.values(serviceData.dailyTotals) as Array<number>;
        const weeklyHours = dailyTotalsValues.reduce((acc, curr) => acc + curr, 0);
        serviceData.possibleSessions.weekly = calculatePossibleSessions(weeklyHours, serviceData.maxDuration, 1);

        const sortedUsers = sortByUserName(serviceData.users) as Array<CategoryUser>;
        serviceData.users = Object.fromEntries(sortedUsers.map((user) => [user.userId, user]));
    });

    return result;
};

const calculatePossibleSessions = (hours: number, serviceDuration: number, timeForSessions: number) => {
    const totalMinutes = hours * 60;
    if (totalMinutes === 0 || !serviceDuration) return 0;
    return Math.floor((totalMinutes / serviceDuration) * timeForSessions);
};

const BACKOFFICE_SERVICE_ID = 9999;

const isServiceMatch = (eventServices: Array<number> = [], selectedServices: Array<number>, eventType: string): boolean => {
    // No services selected - show all events
    if (selectedServices.length === 0) {
        return true;
    }

    // Time off events should always pass service filter (they'll be filtered by type filter if needed)
    if (eventType === "time_off") {
        return true;
    }

    // Event has no services assigned - consider it backoffice work
    if (eventServices.length === 0) {
        return selectedServices.includes(BACKOFFICE_SERVICE_ID);
    }

    // Event has services - check if any match the selected services
    return eventServices.some((serviceId) => selectedServices.includes(serviceId));
};

const isUserMatch = (eventUserId: string, selectedUserIds: Array<string>, eventType: string): boolean => {
    // No users selected - show all events
    if (selectedUserIds.length === 0) {
        return true;
    }

    // Check if event user matches selected users (applies to all event types)
    return selectedUserIds.includes(eventUserId);
};

const isClinicMatch = (eventClinicId: number, selectedClinicIds: Array<number>): boolean => {
    // No clinics selected - show all events
    if (selectedClinicIds.length === 0) {
        return true;
    }

    // Check if event clinic matches selected clinics
    return selectedClinicIds.includes(eventClinicId);
};

export const useFilteredSchedules = (events: Array<CustomEventType>) => {
    const {
        schedules: { eventTypeFilter, servicesSelected, userIds, clinicIds },
    } = useStore();

    if (!events) return [];

    const filteredEvents = events.filter((event) => {
        const typeMatch = eventTypeFilter === "all" || event.type === eventTypeFilter;
        const serviceMatch = isServiceMatch(event.services, servicesSelected, event.type);
        const userMatch = isUserMatch(event.userId, userIds, event.type);
        const clinicMatch = isClinicMatch(event.clinicId, clinicIds);

        return typeMatch && serviceMatch && userMatch && clinicMatch;
    });

    return filteredEvents;
};

export const consolidateEventsForMonthView = (filteredEvents: Array<CustomEventType>) => {
    const groupedByUserAndDay = groupBy(filteredEvents, (event) => `${event.userId}-${format(event.start, "yyyy-MM-dd")}`);

    const consolidatedEvents = Object.values(groupedByUserAndDay).map((dayEvents) => {
        const firstEvent = dayEvents[0];
        const allServices = dayEvents.flatMap((event) => {
            if (event.services && event.services.length > 0) {
                return event.services;
            }
            if (event.type === "schedule") {
                return [0]; // Special ID for "no service schedule event"
            }
            return [];
        });
        const uniqueServices = [...new Set(allServices)];

        const consolidatedEvent = {
            ...firstEvent,
            services: uniqueServices,
        };

        return consolidatedEvent;
    });

    const sortedEvents = sortBy(consolidatedEvents, (event) => event.userName);

    return sortedEvents;
};

export const getSessionCountsByCategory = (categoryIds: ReadonlyArray<number>, sessionsData: any) => {
    const validStatuses = ["booked", "confirmed", "done"];

    const sessionCounts = {
        daily: {} as Record<string, number>,
        total: 0,
    };

    if (!sessionsData?.items) {
        return sessionCounts;
    }

    const validSessions = sessionsData.items.filter(
        (session: any) => categoryIds.includes(session.service) && validStatuses.includes(session.status)
    );

    // Initialize daily counts
    workDays.forEach((day) => {
        sessionCounts.daily[day] = validSessions.filter((session: any) => {
            return format(new Date(session.start_date), "EEEE") === day;
        }).length;
    });

    sessionCounts.total = validSessions.length;

    return sessionCounts;
};

export const customSlotPropGetter = (date: Date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const timeInMinutes = hours * 60 + minutes;

    const lunchStartInMinutes = CLINIC_BREAKS.lunch.start.hours * 60 + CLINIC_BREAKS.lunch.start.minutes;
    const lunchEndInMinutes = CLINIC_BREAKS.lunch.end.hours * 60 + CLINIC_BREAKS.lunch.end.minutes;

    if (timeInMinutes >= lunchStartInMinutes && timeInMinutes < lunchEndInMinutes) {
        return {
            style: {
                backgroundColor: "#D67171",
            },
            "data-lunch": "true",
        };
    }
    return {};
};

export const getFilteredResources = (resources: Array<any> | undefined, selectedIds: Array<number>) => {
    if (!resources) return [];
    return selectedIds.length === 0 ? resources : resources.filter((resource) => selectedIds.includes(resource.id));
};

export const getRoomIdsByType = (resources: Array<any> | undefined, type: "nfb" | "psy"): Array<number> => {
    if (!resources) return [];
    const backOfficeRoomId = resources.find((room) => room.title === "BO")?.id;
    const roomIds = resources.filter((room) => room.type === type).map((room) => room.id);
    const roomIdsWithBackOffice = backOfficeRoomId ? [...roomIds, backOfficeRoomId] : roomIds;
    return roomIdsWithBackOffice;
};

export const calculateDaysWithHours = (dailyTotals: Record<string, number>) => {
    let daysWithHours = 0;

    for (const day in dailyTotals) {
        if (dailyTotals[day] > 0) {
            daysWithHours++;
        }
    }

    const result = daysWithHours;
    return result;
};


export const  timeOffTypes = [
    { label: "Férias", value: "holiday" },
    { label: "Baixa", value: "sick_leave" },
    { label: "Folga", value: "day_off" },
    { label: "Feriado", value: "public_holiday" },
    { label: "Intervalo", value: "break" },
    { label: "Falta justificada", value: "missed_justified" },
    { label: "Falta não justificada", value: "missed_not_justified" },
];

// Helper function to check if a date is a holiday
export const isPublicHolidayDate = (date: Date, holidays: Array<Holiday>): boolean => {
    if (!holidays || holidays.length === 0) return false;

    const dateString = format(date, "yyyy-MM-dd");
    const result = holidays.some(holiday => {
        const holidayZoned = utcToZonedTime(parseISO(holiday.date), TIMEZONE);
        return format(holidayZoned, "yyyy-MM-dd") === dateString;
    });

    return result;
};

// Main day prop getter function for calendar styling
export const createDayPropGetter = (holidays: Array<Holiday>) => {
    return (date: Date) => {
        const styles: any = {};
        const dataAttributes: any = {};
        
        // Check if date is a holiday
        if (isPublicHolidayDate(date, holidays)) {
            styles.backgroundColor = "rgba(255, 192, 203, 0.3)"; // Light pink background
            dataAttributes["data-holiday"] = "true";
        }
        
        // Future validations can be added here, for example:
        // if (isWeekend(date)) {
        //     styles.backgroundColor = "rgba(200, 200, 200, 0.2)";
        //     dataAttributes["data-weekend"] = "true";
        // }
        
        return {
            style: styles,
            ...dataAttributes,
        };
    };
};